import { Box } from "@mui/material";
import PlanSelector from "../planSelector/PlanSelector";
import { Currency, PlanCostModelViewModel, PlanType } from "../Purchase.types";
import { DotLegalHeader, DotLegalLink } from "@dotlegal/dotlegal-ui-components";
import usePlanStepHooks from "../planStep/PlanStep.hooks";
import { IPlanStep } from "../planStep/PlanStep";
import { useTranslation } from "../../localization/useTranslation";
import React from "react";
import Grid from "@mui/material/Grid2";
import AiFeatureTermsDialog from "../aiFeatureTermsDialog/AiFeatureTermsDialog.tsx";

export interface IAddonStep extends Omit<IPlanStep, "refetch"> {}

function AddonStep(props: IAddonStep) {
    const { onSelectedPlanChange, showTermsDialog, setShowTermsDialog, onTermsAccepted } = usePlanStepHooks(props.saveModel, props.onChange);
    const { translatePlanType } = useTranslation();

    const addons = [
        PlanType.AiFeatures,
        PlanType.SingleSignOn,
        PlanType.UserManagementPremium,
        PlanType.CustomLists,
        PlanType.OrganizationalManagement,
        PlanType.Cvr,
        PlanType.Obligations,
        PlanType.EntityTemplates,
        PlanType.SearchService,
        PlanType.PolicyManagement,
        PlanType.EnterpriseRiskManagement,
        PlanType.PublicApi,
    ];

    function onChooseClick(planType: PlanType) {
        onSelectedPlanChange(planType);
    }

    function getSelectorByPlanType(planType: PlanType) {
        const plan = props.plans.find((x) => x.planType === planType)!;
        const costs = getCost(plan);

        switch (plan.planType) {
            case PlanType.SingleSignOn:
                return (
                    <PlanSelector
                        header={translatePlanType(plan.planType)}
                        tooltip={
                            "Our software supports Single Sign-On (SSO), allowing users to access the platform securely with their existing credentials. SSO streamlines the login process so users can authenticate through their organisation’s identity provider, reducing the need to manage multiple usernames and passwords."
                        }
                        costs={"Free"}
                        currency={props.saveModel.currency}
                        subHeader={"More security without extra costs."}
                        features={[
                            "Increased security",
                            "Simplified access management",
                            "Seamless user experience",
                            readMore("https://www.dotlegal.com/en/product/single-sign-on"),
                        ]}
                        showTrialButton={false}
                        purchased={!plan.canPurchasePlan}
                        isSelected={props.saveModel.selectedPlans.includes(plan.planType)}
                        onChooseClick={() => onSelectedPlanChange(plan.planType)}
                        isAddon
                        canPurchase={props.canPurchase}
                    />
                );
            case PlanType.UserManagementPremium:
                return (
                    <PlanSelector
                        header={translatePlanType(plan.planType)}
                        tooltip={
                            "With advanced user and role management, you have the power to design highly specific user roles as an administrator. Control both the features and data each user can access, ensuring that your compliance work is both efficient and secure. You can create as many roles as you want. You can also use the add-on to transfer responsibility from one user to another."
                        }
                        costs={costs}
                        currency={props.saveModel.currency}
                        subHeader={"Advanced user permissions and roles."}
                        features={[
                            "Flexible role design",
                            "Granular access control",
                            "Focused user interfaces",
                            readMore("https://www.dotlegal.com/en/product/advanced-user-management"),
                        ]}
                        showTrialButton={false}
                        purchased={!plan.canPurchasePlan}
                        isSelected={props.saveModel.selectedPlans.includes(plan.planType)}
                        onChooseClick={() => onChooseClick(plan.planType)}
                        isAddon
                        canPurchase={props.canPurchase}
                    />
                );
            case PlanType.CustomLists:
                return (
                    <PlanSelector
                        header={translatePlanType(plan.planType)}
                        tooltip={
                            "Get a tailored overview of your compliance documentation. With Custom Lists, you can create dynamic lists that draw from all your data within the platform – entirely on your own terms. Fast and intuitive. Customise your own lists for the platform and export to Excel."
                        }
                        costs={costs}
                        currency={props.saveModel.currency}
                        subHeader={"Customise lists and export to Excel."}
                        features={[
                            "Fast and dynamic data extraction",
                            "Automatic updated lists",
                            "Lists that match your workflow",
                            readMore("https://www.dotlegal.com/en/product/custom-lists"),
                        ]}
                        showTrialButton={false}
                        purchased={!plan.canPurchasePlan}
                        isSelected={props.saveModel.selectedPlans.includes(plan.planType)}
                        onChooseClick={() => onChooseClick(plan.planType)}
                        isAddon
                        canPurchase={props.canPurchase}
                    />
                );
            case PlanType.Declarations:
                return (
                    <PlanSelector
                        header={translatePlanType(plan.planType)}
                        tooltip={
                            "Take full control of your declarations, all in one place.. Gain a clear overview of your progress across multiple declarations like ISAE3402, ISAE3000, and ISO27001."
                        }
                        costs={costs}
                        currency={props.saveModel.currency}
                        subHeader={"Handle declarations such as ISAE3402."}
                        features={[
                            "Maintain declarations with ease",
                            "Systematic approach",
                            "Give auditors direct access",
                            readMore("https://www.dotlegal.com/en/product/declaration"),
                        ]}
                        showTrialButton={false}
                        purchased={!plan.canPurchasePlan}
                        isSelected={props.saveModel.selectedPlans.includes(plan.planType)}
                        onChooseClick={() => onChooseClick(plan.planType)}
                        isAddon
                        canPurchase={props.canPurchase}
                    />
                );
            case PlanType.OrganizationalManagement:
                return (
                    <PlanSelector
                        header={translatePlanType(plan.planType)}
                        tooltip={
                            "Make documentation for multiple group companies in the same environment. Make a combination of group specific documentation and company specific documentation."
                        }
                        costs={"Get a quote"}
                        currency={props.saveModel.currency}
                        subHeader={"Manage your entire organisation."}
                        features={[
                            "Centralized documentation",
                            "Shared resources & processes",
                            "Flexible management",
                            readMore("https://www.dotlegal.com/en/product/organisational-management"),
                        ]}
                        showTrialButton={false}
                        purchased={!plan.canPurchasePlan}
                        isSelected={props.saveModel.selectedPlans.includes(plan.planType)}
                        onChooseClick={() => window.open("https://www.dotlegal.com/en/about/contact-us", "_blank")}
                        purchaseButtonText="Contact"
                        isAddon
                        canPurchase={props.canPurchase}
                    />
                );
            case PlanType.Cvr:
                return (
                    <PlanSelector
                        header={translatePlanType(plan.planType)}
                        tooltip={
                            "Search by CVR number or company name to instantly fetch and prefill verified business details from the Danish Central Business Register."
                        }
                        costs={costs}
                        currency={props.saveModel.currency}
                        subHeader={"Fetch CVR data instantly."}
                        features={["Effortless legal entity creation", "Search for CVR number or business name", "Prefilled company details"]}
                        showTrialButton={false}
                        purchased={!plan.canPurchasePlan}
                        isSelected={props.saveModel.selectedPlans.includes(plan.planType)}
                        onChooseClick={() => onChooseClick(plan.planType)}
                        isAddon
                        canPurchase={props.canPurchase}
                    />
                );
            case PlanType.Obligations:
                return (
                    <PlanSelector
                        header={translatePlanType(plan.planType)}
                        tooltip={
                            "Overview and tracking of key obligations agreed in your contracts – including deadlines, responsible parties, and fulfilment status."
                        }
                        costs={costs}
                        currency={props.saveModel.currency}
                        subHeader={"Track and manage contract obligations"}
                        features={[
                            "See who’s responsible for what – and by when",
                            "Get notified before obligations are due",
                            "Stay compliant with status tracking",
                            readMore("https://www.dotlegal.com/en/product/obligations"),
                        ]}
                        showTrialButton={false}
                        purchased={!plan.canPurchasePlan}
                        isSelected={props.saveModel.selectedPlans.includes(plan.planType)}
                        onChooseClick={() => onChooseClick(plan.planType)}
                        isAddon
                        canPurchase={props.canPurchase}
                    />
                );
            case PlanType.EntityTemplates:
                return (
                    <PlanSelector
                        header={translatePlanType(plan.planType)}
                        tooltip={
                            "Templates provide a standardised framework that ensures consistency, efficiency, and compliance. As part of the creation flow, you can define the steps and input fields needed to tailor each template to your specific needs."
                        }
                        costs={costs}
                        currency={props.saveModel.currency}
                        subHeader={"Create a standardised structure for your contracts and assets"}
                        features={[
                            "Create reusable templates",
                            "Define custom input fields",
                            "Manage workflow steps",
                            readMore("https://www.dotlegal.com/en/product/custom-contract-templates"),
                        ]}
                        showTrialButton={false}
                        purchased={!plan.canPurchasePlan}
                        isSelected={props.saveModel.selectedPlans.includes(plan.planType)}
                        onChooseClick={() => onChooseClick(plan.planType)}
                        isAddon
                        canPurchase={props.canPurchase}
                    />
                );
            case PlanType.SearchService:
                return (
                    <PlanSelector
                        header={translatePlanType(plan.planType)}
                        tooltip={"Search your entire document library. Find text in PDFs, Word, Excel, PowerPoint, scanned documents and images."}
                        costs={costs}
                        currency={props.saveModel.currency}
                        subHeader={"Search in all your documents across the platform."}
                        features={[
                            "Full-text search across documents",
                            "See document type",
                            "Quick access to relevant content",
                            "Search in PDFs, Word, Excel, PowerPoint, scanned documents and images",
                            readMore("https://www.dotlegal.com/en/product/document-search-and-ocr"),
                        ]}
                        showTrialButton={false}
                        purchased={!plan.canPurchasePlan}
                        isSelected={props.saveModel.selectedPlans.includes(plan.planType)}
                        onChooseClick={() => onChooseClick(plan.planType)}
                        isAddon
                        canPurchase={props.canPurchase}
                    />
                );
            case PlanType.PolicyManagement:
                return (
                    <PlanSelector
                        header={translatePlanType(plan.planType)}
                        tooltip={"Manage your policies end-to-end, from creation and version control to distribution and sign-off tracking."}
                        costs={costs}
                        currency={props.saveModel.currency}
                        subHeader={"Create and manage policies across the organisation."}
                        features={[
                            "Create and edit policies directly in the platform",
                            "Link policy sections to controls",
                            "Version control with full history and version comparison",
                            "Distribute policies and collect sign-offs with reminders",
                        ]}
                        showTrialButton={false}
                        purchased={!plan.canPurchasePlan}
                        isSelected={props.saveModel.selectedPlans.includes(plan.planType)}
                        onChooseClick={() => onChooseClick(plan.planType)}
                        isAddon
                        canPurchase={props.canPurchase}
                    />
                );
            case PlanType.EnterpriseRiskManagement:
                return (
                    <PlanSelector
                        header={translatePlanType(plan.planType)}
                        tooltip={
                            "Gain a complete overview of risk across your organisation. Assess processes, assets, and vendors, track mitigation plans and deadlines, and let risk propagate automatically through linked entities."
                        }
                        costs={costs}
                        currency={props.saveModel.currency}
                        subHeader={"Manage risk across your entire organisation."}
                        features={[
                            "Assess processes, assets, and vendors",
                            "From operational assessment to board-ready report",
                            "Risk spreads automatically through linked entities",
                            "Track mitigation plans, deadlines, and follow-ups",
                        ]}
                        showTrialButton={false}
                        purchased={!plan.canPurchasePlan}
                        isSelected={props.saveModel.selectedPlans.includes(plan.planType)}
                        onChooseClick={() => onChooseClick(plan.planType)}
                        isAddon
                        canPurchase={props.canPurchase}
                    />
                );
            case PlanType.PublicApi:
                return (
                    <PlanSelector
                        header={translatePlanType(plan.planType)}
                        tooltip={
                            "Connect your own systems and workflows to the .legal platform through our public API. Manage legal entities, contracts and assets, including business areas, custom fields and suppliers, and pull entity templates so your integration always knows the shape of your data."
                        }
                        costs={costs}
                        currency={props.saveModel.currency}
                        subHeader={"Connect your own systems and workflows directly to your data on legal entities, contracts and assets."}
                        features={[
                            "Manage legal entities",
                            "Create and update contracts",
                            "Manage assets and suppliers",
                            "Work with business areas and entity templates",
                            readMore("https://www.dotlegal.com/en/product/public-api"),
                        ]}
                        showTrialButton={false}
                        purchased={!plan.canPurchasePlan}
                        isSelected={props.saveModel.selectedPlans.includes(plan.planType)}
                        onChooseClick={() => onChooseClick(plan.planType)}
                        isAddon
                        canPurchase={props.canPurchase}
                    />
                );
            case PlanType.AiFeatures: {
                const isSelected = props.saveModel.selectedPlans.includes(plan.planType);
                return (
                    <PlanSelector
                        header={translatePlanType(plan.planType)}
                        tooltip={"Access our AI-powered features within the platform. More AI capabilities will be added over time."}
                        costs={"Usage-based cost"}
                        currency={props.saveModel.currency}
                        subHeader={"Access AI-powered tools across the platform."}
                        features={[
                            "AI capabilities integrated throughout the platform",
                            "AI Compliance Assistant",
                            "AI Quality Scan",
                            "AI Contract Creation",
                            readMore("https://www.dotlegal.com/en/product/ai-features/"),
                        ]}
                        showTrialButton={false}
                        purchased={!plan.canPurchasePlan}
                        isSelected={isSelected}
                        onChooseClick={isSelected ? () => onSelectedPlanChange(plan.planType) : () => setShowTermsDialog(plan.planType)}
                        isAddon
                        canPurchase={props.canPurchase}
                    />
                );
            }
        }
    }

    function readMore(url: string) {
        return (
            <DotLegalHeader marginBottom={0} fontWeight={400} headerStyle="extraSmall">
                Read more{" "}
                <Box component={"span"} sx={{ "& a.MuiBox-root": { textDecoration: "underline !important" } }}>
                    <DotLegalLink linkColor={"primary"} to={url}>
                        here
                    </DotLegalLink>
                </Box>
            </DotLegalHeader>
        );
    }

    function getCost(plan: PlanCostModelViewModel) {
        if (props.saveModel.currency === Currency.DKK) {
            return plan.totalCostDKK!;
        } else {
            return plan.totalCostEUR!;
        }
    }

    return (
        <>
            <Grid container columnSpacing={2} rowSpacing={2}>
                {addons.map((x) => (
                    <Grid
                        key={x.toString()}
                        size={{
                            xs: 12,
                            sm: 6,
                            md: 4,
                            lg: 4,
                            xl: 3,
                        }}
                    >
                        {getSelectorByPlanType(x)}
                    </Grid>
                ))}
            </Grid>

            {showTermsDialog && (
                <AiFeatureTermsDialog currency={props.saveModel.currency} onClose={() => setShowTermsDialog(undefined)} onAccept={onTermsAccepted} />
            )}
        </>
    );
}

export default AddonStep;
